'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import classes from './VerticalCard.module.scss';
import { Badge, Button, Card, Group, Text } from '@mantine/core';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import { useAOS } from '@/src/lib/hooks/useAOS';
import { Article, Product } from '@/src/types/Header';

export function VerticalCard({ article, product }: { article?: Article; product?: Product }) {
  const dictionary = useDictionary();
  const cardData = article ? article : product;
  const params = useParams();
  const lang = (params.lang ?? 'fr') as 'fr' | 'en';
  useAOS();

  const date = new Date(article ? (cardData as Article)?.createdAt : '');
  const formattedDate = article
    ? new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(date)
    : '';

  const link = article
    ? `/articles/${encodeURIComponent(article?.slug ?? '')}`
    : // @ts-ignore
    `/products/${product?.title?.[lang] ? product.title[lang].replaceAll(' ', '-') : product?.title?.replaceAll(' ', '-')}`;

  // Title/desc may be localized or string
  // @ts-ignore
  const title = cardData?.title?.[lang] ?? cardData?.title;
  // @ts-ignore
  const desc = cardData?.description?.[lang] ?? cardData?.description;

  return (
    <Card withBorder className={classes.card} data-aos="fade-up">
      <Card.Section className={classes.imageSection}>
        {article && (
          <Badge color="grey" className={classes.date}>
            {formattedDate}
          </Badge>
        )}

        <Image
          src={(cardData as any)?.mainImage ?? ''}
          alt={title ?? ''}
          fill
          sizes="(max-width: 768px) 100vw, 33vw"
          style={{ objectFit: 'cover' }}
        />
      </Card.Section>

      {/* Content block */}
      <div className={classes.content}>
        <div className={classes.top}>
          <Text fw={800} className={classes.title} lineClamp={2}>
            {title}
          </Text>

          <Text fz="xs" c="dimmed" className={classes.meta}>
            {article ? dictionary.articles.label : dictionary.products.label}
          </Text>
        </div>

        <Text fz="sm" c="dimmed" className={classes.description} lineClamp={3}>
          {desc}
        </Text>

        {/* Bottom pinned */}
        <div className={classes.bottom}>
          <Group gap={16} className={classes.bottomRow}>
            {product && (cardData as any)?.price ? (
              <div className={classes.priceBlock}>
                <Text fz="xl" fw={900} className={classes.price}>
                  {(cardData as any)?.price} €
                </Text>
                <Text fz="xs" c="dimmed" className={classes.priceHint}>
                  Prix indicatif
                </Text>
              </div>
            ) : (
              <div />
            )}

            <Link href={link} className={classes.link}>
              <Button className={classes.button}>
                {article ? dictionary.articles.readMore : dictionary.products.readMore}
              </Button>
            </Link>
          </Group>
        </div>
      </div>
    </Card>
  );
}
